// src/api/secondLife.js
// Second-Life-Markt — die Bestandsansicht und ihre Handlungen.
// Backend: backend/second_life.py §P5 (Migrationen 294/295) und
// backend/second_life_bestand.py.
// Siehe docs/features/second-life.md fuer den Vertrag.
//
// 🔴 **`listSecondLifeQueue` ist am 2026-10-10 entfallen.** Die Warteschlange
// (`SecondLifeQueue.jsx`) ist stillgelegt; `/second-life` ist die
// Bestandsansicht. Die Backend-Route `GET /api/second-life/queue` ist am
// 2026-10-10 ebenfalls geloescht worden (sie hatte keinen Aufrufer mehr) — der
// Pfad bleibt weg: `backend/tests/test_second_life_queue_path_stays_deleted.py`.
//
// 🔴 `approve` kann mit 409 antworten, wenn das Veroeffentlichen gesperrt ist
// (SECOND_LIFE_VEROEFFENTLICHEN_AKTIV). Das ist KEIN Fehler im Sinne der
// Oberflaeche — es ist die Antwort auf eine unbeantwortete Frage (M1: behaelt
// ein DRAFT die Kanaele?). Die Seite zeigt sie als Hinweis, nicht als Absturz.
import { api } from './client';

// Teil 1 (2026-10-04): die Freigabe nimmt KEINEN Preis mehr — sie rechnet ihn aus
// dem bestaetigten Rabatt und dem live gelesenen A-Preis. `bestaetigt` antwortet auf
// ein `409 a_preis_geaendert` (der A-Preis hat sich seit dem Anlegen geaendert).
//
// 🔴 **`description` ist am 2026-10-09 aus dem Vertrag entfernt worden** (Grill
// W5 der Runde 4). Die Route liest den Schluessel nicht mehr und weist ihn mit
// **400 `description_entfernt`** ab (`backend/second_life.py:1802`); der
// richtige Weg fuer den Entwurfstext ist `POST /<id>/beschreibung`
// (`beschreibungSchreiben` unten). Der Parameter war bis dahin ein Angebot auf
// eine Zusage, die der Server ausdruecklich verweigert — kein Aufrufer nutzte
// ihn, aber der naechste haette darauf gebaut.
export function approveSecondLife(id, { bestaetigt, decidedBy } = {}) {
  const body = {};
  if (bestaetigt) body.bestaetigt = true;
  if (decidedBy) body.decided_by = decidedBy;
  return api.post(`/api/second-life/${id}/approve`, body).then((r) => r.data);
}

// Rabatt in Prozent bestaetigen oder aendern. Auf einem angelegten Entwurf (und
// Schalter an) schreibt der Server Preis, Vergleichspreis und EK sofort in den
// Shopify-Entwurf (`in_shopify: true`); sonst ist die Antwort eine Vorschau
// (`vorschau: true`, bei Schalter aus zusaetzlich `hinweis: "anlage_aus"`).
export function setRabatt(id, rabattPct) {
  return api.post(`/api/second-life/${id}/rabatt`, { rabatt_pct: rabattPct }).then((r) => r.data);
}

// „Suggest again": der Server fragt das Modell erneut und legt NUR den Vorschlag ab.
export function rabattVorschlag(id) {
  return api.post(`/api/second-life/${id}/rabatt-vorschlag`).then((r) => r.data);
}

// 🔴 Der DRITTE Schritt. Bis zum 2026-10-01 hatte `lege_listing_an` keinen
// Aufrufer — die Kette endete nach der Aggregation. Diese Route legt das
// Shopify-DRAFT an; veroeffentlicht wird es erst mit `approveSecondLife`.
//
// ⚠️ 409 `anlage_aus` heisst: der Schalter steht nicht. Das ist kein Fehler im
// Sinne der Oberflaeche — es ist die Antwort auf eine unbeantwortete Frage.
export function createSecondLife(id) {
  return api.post(`/api/second-life/${id}/anlegen`).then((r) => r.data);
}

export function discardSecondLife(id) {
  return api.post(`/api/second-life/${id}/discard`).then((r) => r.data);
}

// 🔴 **Der Weg zu einer Zeile OHNE RMA-Fall** (2026-10-14).
//
// Eine Kladde (`second_life_listings`) entstand bis dahin ausschliesslich aus
// einem `rma_cases`-Fall (`erkenne`) oder als Aggregat ueber vorhandene
// `unit`-Zeilen (`aggregiere`) — beide brauchen eine Retoure. Liegt bei LSM
// B-Ware, ohne dass je eine Retoure dazu erfasst wurde, gab es keine Zeile und
// damit keinen Rabattweg (`POST /<id>/rabatt` nimmt eine Kennung) und keinen
// Anlegeweg. Owner, 2026-10-14: *„Wir brauchen keinen Case, damit wir eine
// B-Ware anlegen duerfen — das macht keinen Sinn."*
//
// `sku` ist die Stufen-SKU der Zeile (`<basis>@<B|C>`), wie sie die
// Bestandsansicht zeigt. Die Zerlegung macht der SERVER
// (`backend/lsm_sku.basis_und_stufe_roh`) — die Oberflaeche baut die
// Zeichenkette nicht nach.
//
// Antwort: `{angelegt, zeile_id, schon_da}`. Fehler, die die Seite kennt:
//   409 `unbekannte_stufe_<X>` — kein oder unbekanntes Suffix (`DEF` inklusive)
//   409 `kein_grundprodukt`    — kein A-Artikel zur Basis-SKU (fail-closed)
//   502 `shopify_lesefehler`   — die Lesung ist GESCHEITERT, nicht „nicht da"
export function kladdeAnlegen(sku) {
  return api.post('/api/second-life/kladde', { sku }).then((r) => r.data);
}

// ── Die Bestandsuebersicht (Teil 2, 2026-10-08) ────────────────────────────
//
// `bestandLesen` liefert `{zeilen, bestand_quelle, beschreibung_aktiv, lsm_fehler,
// bestand_fehler, geholt_am}`.
// Kein LIMIT — die Warteschlange deckelt bei 200, eine Uebersicht nicht. Die
// Zeilenform ist der Vertrag zwischen Route und Seite; sie steht in
// docs/superpowers/plans/2026-10-08-second-life-bestandstabelle.md §"Die Zeilenform".
//
// 🔴 **Die zwei Ausfallgruende** — je `null` (gelesen) oder ein Kurzgrund
// (`Typ: Meldung`, hoechstens 300 Zeichen): `lsm_fehler` (seit 2026-10-10, das
// LSM-Portal antwortete nicht) und `bestand_fehler` (seit 2026-10-11, Shopifys
// Bestand war nicht lesbar). Beide lassen die Tabelle stehen; die Seite nennt sie
// mit `LSM_AUSFALL_HINWEIS` bzw. `BESTAND_AUSFALL_HINWEIS`. ⚠️ `bestand_fehler` ist
// KEIN Code fuer `fehlersatz` — `shopify_fehler` dort ist der Preis-Schreibweg.
//
// 🔴 **`beschreibung_aktiv` ist neu (Grill W7, 2026-10-09).** Der Schalter
// `SECOND_LIFE_BESCHREIBUNG_AKTIV` steht auf prod AUS, und `POST /<id>/beschreibung`
// prueft ihn vor allem anderen — auch vor der Vorschau. Ohne dieses Feld konnte die
// Seite nur bedienbare Knoepfe anbieten, die in eine Absage liefen.
export function bestandLesen() {
  return api.get('/api/second-life/bestand').then((r) => r.data);
}

// 🔴 **Die Produktvorschau eines Paares `sku` + `grade`** (S3, 2026-11-03).
// Lesend; adressiert ueber das PAAR, nicht ueber die Zeilen-ID — sonst haetten
// die kladdenfreien Faelle (`id == null`) keine Vorschau, und genau die brauchen
// sie. Antwortet 200 mit `grund` (`kein_a_produkt` / `keine_shopify_id`) fuer
// eine DATENLAGE; nur ein echter Shopify-Lesefehler ist 502.
export function vorschauLesen(sku, grade) {
  return api
    .get('/api/second-life/vorschau', { params: { sku, grade } })
    .then((r) => r.data);
}

// Schreibt die Beschreibung in den Shopify-ENTWURF (nicht in die Veroeffentlichung).
// Der Server erkennt eine Fremdaenderung und antwortet dann mit 409 — das ist
// kein Fehler im Sinne der Oberflaeche, sondern die Antwort auf einen Konflikt.
//
// 🔴 **Der Rumpfschluessel heisst `text`, nicht `description`.** Die Route liest
// `daten.get("text")` (`backend/second_life.py:2272`); mit `description` endete
// **jeder** Speicherversuch mit `409 kein_text`, noch vor der Shopify-Lesung.
// Das war Blocker B1 des Grills — moeglich, weil keine Probe den Vertrag
// zwischen dieser Datei und der Route anfasste. Die Probe
// `src/api/secondLife.contract.test.js` haelt ihn jetzt fest.
//
// 🔴 **`stand` faellt aus dem Vertrag (Runde 3, Blocker B1).** Die Seite hat den
// Vergleichswert frueher SELBST hergeleitet (`zeile.desc_text` =
// `description_draft`) und mitgesendet — verglichen hat die Route ihn aber gegen
// die LIVE-Beschreibung, und die baut `lege_listing_an` als WRAPPER um genau
// diesen Entwurf (`_beschreibung_html` = Zustandsabsatz + A-Beschreibung,
// `backend/second_life.py:778-790`, geschrieben `:1054`). Fuer jede von POG
// angelegte Zeile waren die zwei Strings damit **strukturell verschieden**: der
// erste Speicherversuch meldete „von Hand geaendert", obwohl niemand etwas
// geaendert hatte. Die Route leitet den Vergleich jetzt SELBST her (live gegen
// das, was POG zuletzt schrieb); der Client sendet nur noch den Text.
//
// `bestaetigt` `true` nach einer `fremdaenderung` — schreibt trotzdem.
export function beschreibungSchreiben(id, text, { bestaetigt } = {}) {  const body = { text };
  if (bestaetigt) body.bestaetigt = true;
  return api.post(`/api/second-life/${id}/beschreibung`, body).then((r) => r.data);
}

// Die Vorschau: DIESELBE Route mit `vorschau: true`. Sie schreibt nichts
// (`status=None`, kein `reconcile_product`) und liefert die **komponierte**
// Fassung — Zustandsabsatz + A-Beschreibung, also das, was im Shop erscheint
// (Spec §8 Frage 3, Owner: „vollstaendig … so wie sie im Shop erscheint").
//
// 🔴 **Nicht lokal aus dem Entwurf bauen.** Der Entwurf ist NUR der
// Zustandsabsatz; die Shop-Beschreibung ist der Wrapper darum. Zwei Wege fuer
// dieselbe Anzeige driften auseinander — der eine zeigt dann etwas, das nie
// geschrieben wird.
export function beschreibungVorschau(id, text) {
  return api
    .post(`/api/second-life/${id}/beschreibung`, { text, vorschau: true })
    .then((r) => r.data?.vorschau ?? '');
}
